import React, { useContext, useState } from 'react';
import { Dialog, CircularProgress, Button, DialogActions, Box, Typography, Grid, TextField, InputAdornment, Input, IconButton } from '@mui/material';
import { useStyles } from './styles';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import KenButton from '../../GlobalComponents/KenButton';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import KenInput from '../../GlobalComponents/KenInput';
import { useNavigate } from 'react-router-dom';
import { campaign } from '../../Utils/apiService';
import { AuthContext } from '../../Helpers/Contexts/authContext';
import { AuthUser } from '../../interfaces/Contexts';
import { enqueueSnackbar } from 'notistack';

interface CampaignDialog {
  isOpen: boolean;
  onClose: () => void;
  onProceed: () => void;
}
const CampaignDialog: React.FC<CampaignDialog> = ({ isOpen, onClose, onProceed }) => {
  const classes = useStyles();
  const {
    state: { user },
  } = useContext(AuthContext);
  const userData = user as AuthUser;
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [campaignTitle, setCampaignTitle] = useState('');
  const [description, setDescription] = useState('');
  const [goalAmount, setGoalAmount] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [base64Image, setBase64Image] = useState('');
  const [imageName, setImageName] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({
    campaignTitle: '',
    description: '',
    goalAmount: '',
    startDate: '',
    endDate: '',
  });

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        setBase64Image(base64String.split(',')[1]); // Remove base64 metadata
        setImageName(file.name);
      };
      reader.readAsDataURL(file); // Convert the file to base64
    }
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value.length > 50) {
      setErrors((prev) => ({ ...prev, campaignTitle: 'Title cannot exceed 50 characters' }));
    } else {
      setErrors((prev) => ({ ...prev, campaignTitle: '' }));
    }
    setCampaignTitle(value);
  };

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value.length > 500) {
      setErrors((prev) => ({ ...prev, description: 'Description cannot exceed 500 characters' }));
    } else {
      setErrors((prev) => ({ ...prev, description: '' }));
    }
    setDescription(value);
  };

  const handleGoalAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (!/^\d*$/.test(value)) {
      setErrors((prev) => ({ ...prev, goalAmount: 'Goal amount must be a valid number' }));
    } else {
      setErrors((prev) => ({ ...prev, goalAmount: '' }));
    }
    setGoalAmount(value);
  };

  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setStartDate(e.target.value);
  };

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEndDate(e.target.value);
  };

  const handlePreventPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    enqueueSnackbar({ message: 'Pasting is not allowed', variant: 'error', autoHideDuration: 1000 });
  };

  const submit = () => {
    setIsSubmitted(true);
    const newErrors = {
      campaignTitle: !campaignTitle.trim() ? 'Title is required' : '',
      description: !description.trim() ? 'Description is required' : '',
      goalAmount: !goalAmount.trim() ? 'Goal amount is required' : '',
      startDate: !startDate ? 'Start date is required' : '',
      endDate: !endDate ? 'End date is required' : '',
    };

    setErrors(newErrors);

    if (Object.values(newErrors).some((error) => error)) {
      return; // Stop submission if there are errors
    }

    if (!goalAmount.trim() || isNaN(parseFloat(goalAmount)) || parseFloat(goalAmount) <= 0) {
      enqueueSnackbar({ message: 'Valid goal amount is required', variant: 'error', autoHideDuration: 1000 });
      return;
    }
    if (new Date(startDate) >= new Date(endDate)) {
      enqueueSnackbar({ message: 'End date must be after start date', variant: 'error', autoHideDuration: 1000 });
      return;
    }
    if (!base64Image) {
      enqueueSnackbar({ message: 'Cover image is required', variant: 'error', autoHideDuration: 1000 });
      return;
    }

    setLoading(true);
    const data = {
      accountId: userData.applicantId,
      campaignTitle,
      base64Image,
      description,
      goalAmount: parseFloat(goalAmount),
      startDate,
      endDate,
      imageName,
    };

    campaign(data)
      .then((res) => {
        if (res?.success === 'true') {
          onProceed();
          enqueueSnackbar({ message: 'Campaign created successfully', variant: 'success', autoHideDuration: 1000 });
        } else {
          enqueueSnackbar({ message: 'Something went wrong, please try again', variant: 'error', autoHideDuration: 1000 });
        }
      })
      .finally(() => setLoading(false));
  };


  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      PaperProps={{
        style: {
          width: '558px',
          margin: 'auto',
          padding: '20px 0px 20px 20px',
          borderRadius: '12px',
        },
      }}
      maxWidth={false}
    >
      <Typography className={classes.sectionTitle}>Campaign details</Typography>
      <div className={classes.sectionDivider}></div>
      <Box sx={{ padding: '0px 20px 0px 0px' }}>
        <Grid
          container
          justifyContent="center"
          alignItems="center"
          sx={{
            height: '99px',
            backgroundColor: '#EAEFFF',
            marginTop: '10px',
            borderRadius: '9px',
            flexDirection: 'column',
          }}
        >
          <div style={{ display: 'flex' }}>
            <Input type="file" style={{ display: 'none' }} onChange={handleFileUpload} inputProps={{ accept: 'image/*' }} id="upload-cover-image-input" />
            <IconButton color="primary" component="label" htmlFor="upload-cover-image-input" sx={{ borderRadius: '50%', marginBottom: '20px' }}>
              <CloudUploadIcon />
            </IconButton>
            <Typography sx={{ marginLeft: '10px', marginTop: '10px', cursor: 'pointer', fontSize: '12px', fontWeight: 500 }}>Upload cover image</Typography>
          </div>
          <Typography sx={{ fontSize: '9px', fontWeight: 400 }}>Max image size: 1 MB | Min width: 1028px | Image format: PNG, JPEG</Typography>
        </Grid>

        <Typography className={classes.sectionTitle1} sx={{ mt: 2 }}>
          Title of the fundraising campaign
        </Typography>
        <TextField
          placeholder="Add title"
          fullWidth
          variant="outlined"
          value={campaignTitle}
          onChange={handleTitleChange}
          error={!!errors.campaignTitle}
          helperText={errors.campaignTitle}
          InputProps={{
            sx: {
              borderRadius: '6px',
              height: '36px',
              fontSize: '12px',
              border: isSubmitted && !campaignTitle ? "1px solid red" : "1px solid #ccc",
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none !important',
              },
            },
          }}
        />
        <TextField
          sx={{ marginTop: '10px' }}
          placeholder="Please provide a detailed explanation of why the funds are needed"
          fullWidth
          variant="outlined"
          multiline
          rows={4}
          value={description}
          onChange={handleDescriptionChange}
          error={!!errors.description}
          helperText={errors.description}
          InputProps={{
            sx: {
              borderRadius: '6px',
              fontSize: '12px',
              border: isSubmitted && !description ? '1px solid red' : '1px solid #ccc',
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none !important',
              },
            },
          }}
        />
        <Typography sx={{ marginTop: '15px' }} className={classes.sectionTitle1}>
          Mention your fundraising goal
        </Typography>
        <TextField
          placeholder="Amount"
          fullWidth
          variant="outlined"
          value={goalAmount}
          onChange={handleGoalAmountChange}
          error={!!errors.goalAmount}
          helperText={errors.goalAmount}
          InputProps={{
            sx: {
              borderRadius: '6px',
              height: '36px',
              fontSize: '12px',
              border: isSubmitted && !goalAmount ? '1px solid red' : '1px solid #ccc',
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none !important',
              },
            },
            endAdornment: (
              <InputAdornment position="end">
                <CurrencyRupeeIcon sx={{ fontSize: '15px' }} />
              </InputAdornment>
            ),
          }}
        />

        <Typography sx={{ marginTop: '15px' }} className={classes.sectionTitle1}>
          Set a timeline
        </Typography>
        <Grid container spacing={2}>
          <Grid item md={6} xs={12}>
            <KenInput
              type="date"
              fullWidth
              variant="outlined"
              value={startDate}
              onChange={handleStartDateChange}
              // error={!startDate} // Add validation condition
              InputProps={{
              onPaste: handlePreventPaste,
              sx: {
                height: '36px',
                fontSize: '12px',
                border: isSubmitted && !startDate ? '1px solid red' : '1px solid #ccc',
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none !important',
                },
              },
              }}
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <KenInput
              type="date"
              fullWidth
              variant="outlined"
              value={endDate}
              onChange={handleEndDateChange}
              // error={!endDate} // Add validation condition
              InputProps={{
              onPaste: handlePreventPaste,
              sx: {
                height: '36px',
                fontSize: '12px',
                border: isSubmitted && !endDate ? '1px solid red' : '1px solid #ccc',
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none !important',
                },
              },
              }}
            />
          </Grid>
        </Grid>
      </Box>

      <DialogActions style={{ marginTop: '15px', paddingRight: '20px' }}>
        <Button onClick={onClose}>
          <Typography sx={{ fontSize: '12px', fontWeight: 500, color: '#373A45' }}>Cancel</Typography>
        </Button>
        <KenButton variant="contained" className={classes.button} style={{ width: '160px', borderRadius: '6px', boxShadow: 'none', height: '36px' }} onClick={submit}>
          <Typography sx={{ fontSize: '12px', fontWeight: 500 }}>Submit request </Typography>
          {loading ? (
            <CircularProgress
              size="15px"
              sx={{
                color: 'white',
                marginLeft: '5px',
              }}
            />
          ) : (
            ''
          )}
        </KenButton>
      </DialogActions>
    </Dialog>
  );
};

export default CampaignDialog;
